import classNames from 'classnames'
import { Headset, PhoneXmark } from 'iconoir-react'
import { ReactElement } from 'react'
import { callEndedBy } from './callEndedBy'

type Props = {
  terminationReason?: string | null
  className?: string
}

/**
 * Milestone divider rendered at the end of the AI Agent inbound call transcript
 * reporting who ended the call, derived from the ElevenLabs post-call
 * `termination_reason`. Renders "Caller Disconnected" when the caller hung up
 * and "Agent Ended Call" when the agent ended it; any other reason (system
 * error, network drop, duration limit) renders nothing.
 *
 * Same horizontal-rule + centered pill shape as the other transcript dividers.
 */
export function CallEndedByDivider({ terminationReason, className }: Props): ReactElement | null {
  const endedBy = callEndedBy(terminationReason)
  if (!endedBy) return null

  const Icon = endedBy === `caller` ? PhoneXmark : Headset
  const label = endedBy === `caller` ? `Caller Disconnected` : `Agent Ended Call`

  return (
    <div
      data-testid="call-ended-by-divider"
      data-ended-by={endedBy}
      className={classNames(`flex items-center`, className)}
    >
      <div className="h-px w-full bg-surface-strong" />
      <div className="flex shrink-0 items-center gap-1 rounded border-[0.5px] border-strong bg-surface-muted px-1.5 py-0.5">
        <Icon className="h-3.5 w-3.5 shrink-0 text-subtle" />
        <span className="whitespace-nowrap text-center text-label-xs font-medium text-subtle">{label}</span>
      </div>
      <div className="h-px w-full bg-surface-strong" />
    </div>
  )
}
